import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BridgeClubLayout from '../../../components/BridgeClub/BridgeClubLayout';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';
import { useBridgeClubAuth } from '../../../contexts/BridgeClubAuthContext';
import './FarmChallengeHub.css';

interface ActivityLog {
  id: number;
  activity_type: string;
  activity_type_display: string;
  activity_date: string;
  base_points: number;
  multiplier: number;
  bonus_points: number;
  total_points: number;
  animal_id: number | null;
  animal_name: string | null;
  activity_data: any;
  created_at: string;
}

interface HistoryResponse {
  logs: ActivityLog[];
  total: number;
  limit: number;
  offset: number;
}

const FarmChallengeHistory: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useBridgeClubAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const loadHistory = useCallback(async (newOffset: number = 0) => {
    try {
      setLoading(true);
      const data: HistoryResponse = await bridgeClubAPI.getFarmChallengeHistory({
        limit,
        offset: newOffset
      } as any);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setOffset(newOffset);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load history:', err);
      // Handle auth errors - redirect to login
      if (err.status === 401 || err.response?.status === 401) {
        navigate('/bridge-club/login', { replace: true });
        return;
      }
      setError('Failed to load activity history');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Wait for auth check to complete before loading history
    if (!authLoading && isAuthenticated) {
      loadHistory();
    } else if (!authLoading && !isAuthenticated) {
      navigate('/bridge-club/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, loadHistory, navigate]);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'egg_log': return '🥚';
      case 'milk_log': return '🥛';
      case 'weight_log': return '⚖️';
      case 'harvest_log': return '🔪';
      case 'purchase': return '🛒';
      case 'consistency_bonus': return '🔥';
      case 'streak_milestone': return '🏆';
      default: return '✨';
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T12:00:00');
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getActivityDetails = (log: ActivityLog) => {
    const data = log.activity_data || {};
    switch (log.activity_type) {
      case 'egg_log':
        return data.egg_count ? `${data.egg_count} eggs` : null;
      case 'milk_log':
        return data.gallons ? `${data.gallons} gallons` : null;
      case 'weight_log':
        return data.weight ? `${data.weight} lbs` : null;
      case 'harvest_log':
        return data.sale_weight ? `${data.sale_weight} lbs` : null;
      default:
        return null;
    }
  };

  if (loading || authLoading) {
    return (
      <BridgeClubLayout>
        <div className="farm-challenge-loading">
          <div className="spinner"></div>
          <p>Loading history...</p>
        </div>
      </BridgeClubLayout>
    );
  }

  if (error && logs.length === 0) {
    return (
      <BridgeClubLayout>
        <div className="farm-challenge-error">
          <p>{error}</p>
          <button onClick={() => loadHistory()}>Try Again</button>
        </div>
      </BridgeClubLayout>
    );
  }

  const hasMore = offset + limit < total;
  const hasPrev = offset > 0;

  return (
    <BridgeClubLayout>
      <div className="farm-challenge-hub">
        <div className="hub-header">
          <Link to="/bridge-club/farm-challenge" className="back-link">
            ← Back
          </Link>
          <h1>Activity History</h1>
        </div>

        {logs.length === 0 ? (
          <div className="activity-empty">
            <p>No activity yet. Start logging to earn points!</p>
            <Link to="/bridge-club/farm-challenge" className="btn-primary">
              Go to Farm Challenge
            </Link>
          </div>
        ) : (
          <>
            <div className="history-summary">
              <p>{total} total {total === 1 ? 'activity' : 'activities'}</p>
            </div>

            <div className="activity-list history-list">
              {logs.map((log) => (
                <div key={log.id} className="activity-item history-item">
                  <span className="activity-icon">{getActivityIcon(log.activity_type)}</span>
                  <div className="activity-info">
                    <span className="activity-title">{log.activity_type_display}</span>
                    {log.animal_name && (
                      <span className="activity-animal">{log.animal_name}</span>
                    )}
                    {getActivityDetails(log) && (
                      <span className="activity-details">{getActivityDetails(log)}</span>
                    )}
                    <span className="activity-date">{formatDate(log.activity_date)}</span>
                  </div>
                  <div className="activity-points-detail">
                    <span className="activity-points">+{log.total_points}</span>
                    {log.multiplier > 1 && (
                      <span className="activity-multiplier">{log.multiplier}x</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="pagination">
              <button
                onClick={() => loadHistory(offset - limit)}
                disabled={!hasPrev || loading}
                className="pagination-btn"
              >
                Previous
              </button>
              <span className="pagination-info">
                {offset + 1}-{Math.min(offset + limit, total)} of {total}
              </span>
              <button
                onClick={() => loadHistory(offset + limit)}
                disabled={!hasMore || loading}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </BridgeClubLayout>
  );
};

export default FarmChallengeHistory;
